/**
 * LiveSessionsListScreen — primary adapter for `/live-sessions-list` ("Tous mes lives").
 *
 * Thin wire: data + view-model + state → DS page + overlays (gestes partagés, message, annulations).
 */
import { useSelector } from "react-redux"

import { ConfirmDialog, LiveSessionsListPage, SendTutorMessageModal } from "../../../../../../design-system"
import { useParentSessionGesture } from "../../../hooks/useParentSessionGesture"
import { type UILiveSession } from "../../../types"
import { liveSessionsListViewModel } from "../../../../view-models/parent/LiveSessions/liveSessionsListViewModel"
import { ParentSessionGestures } from "../shared/ParentSessionGestures"
import { useLiveSessionsListData } from "./useLiveSessionsListData"
import { useLiveSessionsListState } from "./useLiveSessionsListState"

export type LiveSessionsListScreenProps = {
    route?: { params?: { student_email?: string } | null } | null
    /**
     * Quitte l'espace parent pour celui de l'enfant, sur cette séance. Tenu par l'enveloppe
     * legacy qui monte cet écran (`LiveSessionsListWithJoin`) : la bascule de compte et la
     * navigation différée vivent dans `src/common/**`, que `ports/**` n'importe pas. ABSENT =
     * pas de ligne « Rejoindre » dans la feuille d'actions.
     */
    onJoinAsChild?: (session: UILiveSession) => void
}

export function LiveSessionsListScreen(props: LiveSessionsListScreenProps) {
    const studentEmail = props.route?.params?.student_email ?? null
    const { refresh } = useLiveSessionsListData(studentEmail)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const vm = useSelector((state: any) => liveSessionsListViewModel(state, studentEmail))
    const { actions, messageModal, cancelConfirm, manageRdv, feedback } = useLiveSessionsListState({ studentEmail })
    const gesture = useParentSessionGesture({
        sessions: vm.sessions,
        surface: "live_sessions_list",
        studentEmail,
        onOpenTutorMessage: actions.openMessageForTutor,
        onCancelFlag: actions.onLiveMenuAction,
        onJoinAsChild: props.onJoinAsChild,
        paymentUrl: vm.paymentUrl,
        refresh,
    })

    return (
        <>
            <LiveSessionsListPage
                groups={vm.groups}
                loading={vm.loading}
                refreshing={vm.refreshing}
                error={vm.error}
                onRetry={refresh}
                onClose={actions.close}
                onRefresh={refresh}
                onSessionAction={gesture.onSessionAction}
                joinAsChildFirstname={gesture.joinAsChildFirstname}
                onBlockedSessionPress={gesture.onBlockedSessionPress}
            />
            <ParentSessionGestures {...gesture.gestures} />
            <SendTutorMessageModal {...messageModal} />
            <ConfirmDialog
                visible={manageRdv != null}
                title={manageRdv?.title ?? ""}
                message={manageRdv?.message}
                confirmLabel={manageRdv?.confirmLabel ?? "OK"}
                cancelLabel={manageRdv?.cancelLabel ?? "Retour"}
                danger={false}
                onConfirm={actions.confirmManageRdv}
                onCancel={actions.cancelManageRdv}
                onDismiss={actions.dismissManageRdv}
            />
            <ConfirmDialog
                visible={cancelConfirm != null}
                title={cancelConfirm?.title ?? ""}
                message={cancelConfirm?.message}
                confirmLabel="Oui, annuler"
                cancelLabel="Non"
                danger
                onConfirm={actions.confirmCancel}
                onCancel={actions.closeCancel}
            />
            <ConfirmDialog
                visible={feedback != null}
                title={feedback?.title ?? ""}
                message={feedback?.message}
                confirmLabel="OK"
                cancelLabel="Fermer"
                danger={false}
                onConfirm={actions.closeFeedback}
                onCancel={actions.closeFeedback}
            />
        </>
    )
}

export default LiveSessionsListScreen
